/** The control center (`/control-center`): the server's own administration. */

export default {
	controlCenter: {
		nav: {
			exit: "Back to Notitia",
			users: "Users",
			roles: "System roles",
			departments: "Departments",
			projects: "Projects",
			dataspaces: "Dataspaces",
			releases: "Releases",
			"service-tokens": "Service tokens",
			runtime: "Runtime",
			maintenance: "Maintenance",
			settings: "Settings",
			license: "License",
			groups: {
				people: "People",
				content: "Content",
				server: "Server",
			},
		},
		users: {
			search: "Search users…",
			empty: "No user matches.",
			roleFor: "System role of {name}",
			changed: "{name} is now {role}.",
			column: {
				user: "User",
				systemRole: "System role",
				projects: "Projects",
				dataspaces: "Dataspaces",
				joined: "Joined",
			},
			banConfirm: "Ban {name}?",
			banNote:
				"They are signed out everywhere at once and cannot sign in again until they are given another role. Their memberships stay.",
			ban: "Ban",
			adminConfirm: "Make {name} a server admin?",
			adminNote:
				"Server admins hold every system permission: they can change anyone's role, every system role, and every project and dataspace.",
			makeAdmin: "Make server admin",
		},
		roles: {
			names: {
				user: "User",
				server_admin: "Server admin",
				banned: "Banned",
			},
			sections: {
				access: "Access",
				controlCenter: "Control center",
			},
			ops: {
				signin: "Sign in",
				inviteUsers: "Invite users",
				mintPermanentPats: "Create personal access tokens that never expire",
				manageUsers: "Manage users",
				manageRoles: "Manage system roles",
				manageProjects: "Manage all projects",
				manageDataspaces: "Manage all dataspaces",
				manageReleases: "Manage all releases: export and import",
				manageServiceTokens: "Create service tokens for the server",
				manageServer: "Run the server: runtime, maintenance, settings and license",
				createDepartments: "Create departments by entering a license key",
				aliasDataspaces: "Name dataspaces with an alias, in the Operator's URLs",
				manageDepartments: "Manage all departments: license keys, owners and deletion",
			},
			userCount: "No users | {n} user | {n} users",
			locked: "Locked",
			noRename:
				"A system role is never renamed: users hold it by name. Duplicate it to start a new one.",
			deleteNote: "Nobody holds it. This cannot be undone.",
		},
		members: {
			add: "Add member",
			addMe: "Add me",
			addTitle: "Add a member",
			user: "User",
			userPlaceholder: "Search by name or email…",
			added: "Added {name}.",
			addedSelf: "You are a member now.",
			roleChanged: "Changed the role of {name}.",
			remove: "Remove {name}",
			removed: "Removed {name}.",
			removeConfirm: "Remove {name}?",
			removeSelfConfirm: "Remove yourself?",
			removeNote:
				"They lose access at once, and their open sessions stop receiving its changes.",
		},
		projects: {
			filesMissing: "Files missing",
			filesMissingTitle: "This project's files are missing on the server",
			filesMissingNote:
				"Its record is all that is left, so nothing in it can be opened. You can remove it for good in Maintenance.",
			filesMissingAskNote:
				"Its record is all that is left, so nothing in it can be opened. A server administrator can remove it for good in Maintenance.",
			search: "Search projects…",
			empty: "No project matches.",
			createdBy: "Created by {name}",
			notMember: "Not a member",
			notMemberHint:
				"You are not a member of this project. Add yourself to open it in the Designer.",
			openDesigner: "Open in Designer",
			noDataspaces: "No dataspace runs this project.",
			deleted: "Deleted {name}.",
			deleteConfirm: "Delete {name}?",
			deleteNote:
				"Every member loses it, and projects that depend on it can no longer resolve it. Its dataspaces keep running the builds they have.",
			column: {
				project: "Project",
				department: "Department",
				version: "Version",
				members: "Members",
				yourRole: "Your role",
				updated: "Updated",
			},
		},
		dataspaces: {
			search: "Search dataspaces…",
			empty: "No dataspace matches.",
			notMemberHint:
				"You are not a member of this dataspace. Add yourself to open it in the Operator.",
			openOperator: "Open in Operator",
			noProcesses: "Nothing has been started here.",
			deleted: "Deleted {name}.",
			deleteConfirm: "Delete {name}?",
			deleteNote:
				"Its running processes are stopped first. Its members, roles, environment and process history go with it. This cannot be undone.",
			column: {
				dataspace: "Dataspace",
				running: "Running",
			},
		},
		departments: {
			search: "Search departments…",
			empty: "No department matches.",
			create: "Create department",
			column: {
				department: "Department",
				license: "License",
				members: "Members",
				projects: "Projects",
				dataspaces: "Dataspaces",
				owners: "Owners",
			},
			createNote:
				"The license key is checked with the license server first. You are not made a member: the owner you name by email is.",
			ownerEmail: "Owner's email",
			ownerEmailHelp:
				"They get an invitation to own the department; until they accept, it has no owner.",
			created: "Created {name}.",
			createdInvited: "Created {name}. {email} is invited to own it.",
			rekeyTitle: "Replace the license key of {name}",
			rekeyed: "Replaced the license key of {name}.",
			ownersTitle: "Owners of {name}",
			ownersNote: "Owners hold every department permission. A department keeps at least one.",
			ownersNone: "This department has no owner yet.",
			makeOwner: "Make owner",
			makeOwnerHelp:
				"A member of the department with this email becomes an owner at once; anyone else gets an invitation to own it.",
			owners: {
				promoted: "{email} is an owner now.",
				invited:
					"{email} is invited to own the department, and is an owner once they accept.",
				demote: "Make {name} a member",
				demoted: "{name} is a member now.",
			},
			rowMenu: {
				owners: "Manage owners",
				rekey: "Replace license key",
				moveIn: "Move spaces in…",
				moveOut: "Make spaces personal…",
			},
			spaces: {
				titleIn: "Move spaces into {name}",
				titleOut: "Make spaces of {name} personal",
				noteIn: "Any project or dataspace on the server can be moved here, whatever you may do with it yourself. One that is in another department leaves that department. Its department access starts over: this department's owners administer it, and nobody else reaches it through the department until roles are mapped. Nothing moves unless all of it can.",
				noteOut:
					"They keep the people added to them by hand and lose the department's. A space with no admin of its own cannot be made personal from here, because the control center has no role of its own to give: add an admin from the project's or the dataspace's page first. Nothing moves unless all of it can.",
				noneIn: "Everything on the server is in this department already.",
				noneOut: "Nothing is in this department.",
				confirmIn: "Move {n} space | Move {n} spaces",
				confirmOut: "Make {n} space personal | Make {n} spaces personal",
				movedIn: "Moved {n} space into {name}. | Moved {n} spaces into {name}.",
				movedOut:
					"{n} space of {name} is personal now. | {n} spaces of {name} are personal now.",
			},
			default: {
				title: "Default department",
				note: "Every user who has an account becomes a member of the default department with the role chosen here, unless they have a membership of their own. So what that role grants is granted to the whole server, through the department's spaces too. Live updates of the spaces follow the new access at once, but nobody is notified individually: an open session learns of the change when its user next moves to another page.",
				department: "Department",
				role: "Role everyone holds",
				roleHelp: "Choose with care: everyone holds it.",
				none: "None",
				saved: "{name} is the default department now.",
				cleared: "There is no default department now.",
			},
			deleted: "Deleted {name}.",
			deleteConfirm: "Delete {name}?",
			deleteNote:
				"Its members and roles go with it. This cannot be undone. A department with a project or a dataspace in it, and the default department, cannot be deleted.",
		},
		releases: {
			search: "Search releases…",
			empty: "No release matches.",
			none: "Nothing on this server has been released yet.",
			column: {
				project: "Project",
				version: "Version",
				origin: "Origin",
				created: "Released",
				build: "Build",
			},
			importedProject: "Imported project",
			import: {
				action: "Import release",
				title: "Import a release",
				note: "A .notitia-release.zip exported from another Notitia server. It is checked before anything is imported.",
				choose: "Choose a file",
				chosen: "{name} · {size}",
				checking: "Checking the archive…",
				project: "Project",
				version: "Version",
				exportedFrom: "Exported from",
				exportedAt: "Exported",
				engine: "Engine",
				node: "Node",
				packages: "Packages",
				sha256: "SHA-256",
				notes: "Notes",
				outcome: {
					newProject:
						"{name} is not on this server. It will arrive as a project known only from its releases, with you as its admin.",
					newVersion:
						"{name} is on this server. v{version} will be added to its releases.",
					identical:
						"This very archive was imported before. Importing it again changes nothing.",
					conflict: "This archive cannot be imported.",
				},
				confirm: "Import",
				done: "Imported {name} v{version}.",
				doneIdentical: "{name} v{version} was here already. Nothing changed.",
				another: "Import another",
				errors: {
					invalidArchive: "This file is not a release archive, or it failed its checks.",
					conflict:
						"This server already has that version of the project, from a different archive.",
				},
			},
			project: {
				none: "This project has no releases.",
				runnable: "Runnable here",
			},
		},
		serviceTokens: {
			description:
				"Service tokens are credentials of this server itself, for unattended consumers such as CI jobs and integrations. Their rights are exactly what they are granted here — never those of the admin who created them, and never bound to a person.",
			mint: {
				action: "Create token",
				title: "Create a service token",
			},
			list: {
				title: "The server's tokens",
				empty: "No service tokens yet. Create one to give a CI job or an integration access.",
				revoked: "Token revoked",
				revoke: {
					title: "Revoke this token?",
					body:
						"Anything using the token “{name}” loses access at once, and this cannot be undone. " +
						"An access token already derived from it keeps working until its own short lifetime ends.",
				},
			},
			error: {
				generic: "Something went wrong with the service tokens. Try again.",
				service_token_not_found: "This token no longer exists. The list has been reloaded.",
				service_token_name_invalid:
					"This name is not accepted. Use 1–100 characters without invisible formatting.",
				service_token_lifetime_exceeded:
					"This expiry is longer than the provider allows. Choose fewer days.",
				service_token_grants_invalid:
					"The permissions were not accepted. A token needs between 1 and 100 valid permissions.",
				service_token_grant_not_available:
					"A permission was refused: the project or dataspace does not exist, or the permission cannot be granted there.",
				service_token_mint_refused:
					"The identity provider refused to create the token. A token without an expiry may not be allowed — set an expiry instead.",
				idp_unavailable: "The identity provider is unreachable. Try again later.",
			},
		},
		runtime: {
			server: "Server",
			version: "Version",
			uptime: "Up for",
			running: "Running processes",
			noProcesses: "No process exists on this server yet.",
			builds: "Builds in progress",
			cache: "Open projects",
			cacheNote: "{n} of at most {max}, each closed after {ttl} minutes idle",
			cacheEmpty: "No project is open.",
			stale: "Reopens when free",
			seconds: "{n} s",
			minutes: "{n} min",
			hours: "{n} h",
			days: "{n} days",
			restart: "Restart {name}",
			restarting: "Restarting {name}.",
			stop: "Stop {name}",
			stopped: "Stopped {name}.",
			stopConfirm: "Stop {name}?",
			stopNote: "Everyone working in {dataspace} sees it stop. Its last state is saved.",
			stopAction: "Stop",
			status: {
				running: "Running",
				starting: "Starting",
				stopped: "Stopped",
				failed: "Failed",
				archived: "Archived",
			},
			column: {
				process: "Process",
				status: "Status",
				restartRule: "Restart rule",
				idle: "Idle",
			},
		},
		maintenance: {
			permanent:
				"Everything removed here is gone for good. Nothing on this page can be undone.",
			size: "Size",
			file: "Name",
			kind: "Kind",
			none: "None",
			nothing: "Nothing to clean up.",
			deleteDataspacesFirst: "Still runs in — delete these first:",
			purgeAll: "Remove {n} for good",
			purgeOne: "Remove {name} for good?",
			purgeMany: "Remove {n} projects for good?",
			purgeBrokenNote:
				"Their records, members, roles and invitations, and whatever is left of their files and builds, are removed. Members see them disappear from their lists. A package another project pins stays.",
			purgeDeletedNote:
				"Their records, files and builds are removed. A package another project pins stays.",
			removeAll: "Remove {n}",
			removeFileOne: "Remove {name}?",
			removeFileMany: "Remove {n} orphaned files?",
			removeFileNote: "Nothing on the server points at them. They are deleted from disk.",
			removeForGood: "Remove for good",
			removed: "Removed {n}. | Removed {n}. | Removed {n}.",
			skipped: "Kept {item}: {why}",
			broken: {
				title: "Broken projects",
				note: "Records whose files are missing or incomplete on the server",
			},
			deleted: {
				title: "Deleted projects",
				note: "Deleted in the app, but still kept on disk",
			},
			files: {
				title: "Orphaned files",
				note: "Directories and files no record points at",
			},
			expired: {
				title: "Expired records",
				hint: "Kept after they stopped working",
				invitations: "Expired invitations",
				confirm: "Remove {n} expired records?",
				note: "Expired invitations no longer work.",
			},
			kinds: {
				project: "Project",
				build: "Build",
				package: "Package",
				process_state: "Process state",
			},
			bytes: {
				b: "{n} B",
				kb: "{n} KB",
				mb: "{n} MB",
				gb: "{n} GB",
			},
		},
		settings: {
			saved: "Saved.",
			discard: "Discard changes",
			saveFirst: "Save or discard your changes to test.",
			invalid: "{field} was not accepted. Check the value and save again.",
			invalidField: "This value was not accepted.",
			change: "Change {label}",
			keepCurrent: "Keep the current one",
			secretKeep: "Leave empty to keep the current one.",
			secretReplaces: "Saving replaces the current one.",
			inherit: "Use the inherited value",
			keepSetHere: "Keep the value set here",
			unreadable:
				"Some stored secrets cannot be decrypted with this server's key file (data/secret.key) and are ignored until they are entered again:",
			source: {
				env: "Inherited from the server's environment ({env}).",
				default: "Built-in default.",
				db: "Set here.",
				unset: "Not set.",
				clearing:
					"Saving removes the value set here; the environment's value or the built-in default applies again.",
			},
			mail: {
				title: "Mail",
				note: "Invitations, password resets and account mails",
				disabled:
					"Mail is not configured. Invitations, password resets and account mails are not sent until a host and a sender address are set.",
				enabled: "Mail is sent from {user} through {host}.",
				dkimOn: "DKIM-signed for {domain}",
				dkimOff: "Not DKIM-signed",
				host: "SMTP host",
				port: "Port (SMTPS)",
				user: "Sender address (SMTP login)",
				password: "SMTP password",
				fromName: "Sender name",
				dkimDomain: "DKIM domain",
				dkimSelector: "DKIM selector",
				dkimKey: "DKIM private key (PEM)",
				test: "Send test mail",
				tested: "A test mail was sent to {to}.",
			},
			invitations: {
				title: "Invitations",
				note: "Whether an invitation to a project, a dataspace or a department is sent by email",
				sendMail: "Send invitations by email",
				on: "On",
				off: "Off",
				enabled: "Invitations are sent by email.",
				disabled:
					"Invitations are not sent by email. Whoever invites is handed the link instead, to pass on themselves, and cannot send an invitation again.",
				noMail: "Invitation mail is on, but no mail is configured above, so none can be sent. Whoever invites is handed the link instead, to pass on themselves.",
			},
			llm: {
				title: "LLM gateway",
				note: "Code assist and terminology suggestions",
				disabled:
					"The LLM gateway is not configured, so code assist and terminology suggestions are off. All four values are needed.",
				enabled: "Code assist and terminology suggestions use {model}.",
				url: "Gateway base URL (https://host, no path)",
				token: "Token",
				model: "Model",
				service: "Service",
				test: "Test connection",
				tested: "The gateway answered in {ms} ms.",
			},
			map: {
				title: "Map",
				note: "The map of a Location question in the Operator — its tiles through this server, never straight from the browser",
				disabled:
					"No map: a Location question shows its address and its suggestions only. Set a tile server inside the network to show one.",
				enabled: "Location questions show a map with tiles from {host}.",
				publicServers:
					"This is a public OpenStreetMap tile server: every map an operator opens is fetched from the OpenStreetMap Foundation, whose policy forbids heavy use. Set a tile server inside the network instead.",
				switch: "Map",
				on: "On",
				off: "Off",
				tileUrl: "Tile server inside the network — base URL (http://host:port, no path)",
				attribution: "Attribution shown on the map",
				test: "Test connection",
				tested: "The tile server answered in {tile} ms.",
			},
			processes: {
				title: "Defaults for processes",
				note: "What every process gets for the NOTITIA_* variables its dataspace leaves empty",
				noGateway:
					"Processes get no LLM gateway: no URL is set here, and the LLM gateway above has none.",
				noToken:
					"Processes get no LLM gateway yet: set a token for them. Without one every request would be refused.",
				derived: "Processes reach the LLM gateway at {url}, the gateway above.",
				set: "Processes reach the LLM gateway at {url}.",
				llmUrl: "Gateway base URL for processes (empty: the LLM gateway's above)",
				llmToken: "Gateway token for processes — their own, not the server's",
				llmModel: "Model (empty: the engine's default)",
				test: "Test as a process",
				tested: "The gateway answered {model} in {ms} ms.",
				tableCaption: "What a process gets for each variable",
				variable: "Variable",
				value: "Value",
				origin: "Source",
				source: {
					db: "control center",
					env: "environment",
					default: "built-in default",
				},
				missing: {
					unset: "not set",
					no_token: "withheld: no token for processes",
					engine_default: "the engine's default",
				},
			},
			location: {
				title: "Location service",
				note: "Address search and the address at a pin in the Operator — the LiveReader location service, inside the network",
				disabled:
					"No location service is set: no address search, and a pin on the map answers with its coordinates only.",
				enabled: "Address search and the address at a pin from {host}.",
				everywhere: "Searching every region it knows.",
				inRegion: "Searching in {region}.",
				outdated:
					"It answered as {version}, older than 1.2.0: addresses are found, but without points on the map, alternatives or the address at a pin, and not while its LLM is down. Update the location service.",
				unknownVersion: "an unnamed version",
				url: "Location service — base URL, inside the network only",
				timeoutMs: "Timeout in milliseconds (10000 when empty)",
				region: "Region to search in (all when empty)",
				test: "Test connection",
				tested: "Location service {version} answered in {ms} ms.",
				testedOutdated:
					"The location service answered in {ms} ms, but it is older than 1.2.0 ({version}).",
			},
		},
		license: {
			title: "License key",
			note: "Checked with the license server every hour",
			valid: "Licensed to {label}.",
			validNote: "Last confirmed {date}.",
			key: "Key",
			id: "License ID",
			label: "Licensed to",
			lastCheck: "Last confirmed",
			graceEnds: "Grace period ends",
			server: "License server",
			features: "Features",
			featureDesigner: "Designer",
			featurePersonalSpaces: "Personal spaces",
			featurePersonalMemberLimit: "Personal member limit",
			featuresDefaults:
				"The license server did not say. These are the defaults, and the next check replaces them if it does.",
			on: "On",
			off: "Off",
			lastError: "The last check failed ({date})",
			newKey: "License key",
			newKeyHelp: "Checked with the license server before it is saved.",
			saveKey: "Check and save",
			changeKey: "Change key",
			checkNow: "Check now",
			saved: "The license key is confirmed and saved.",
			checked: "The license server confirmed the license.",
			notice: {
				missing: "No license: the Designer and the Operator are closed.",
				missingNote: "Enter the license key LiveReader issued for this server.",
				expired:
					"No confirmed license since {date}: the Designer and the Operator are closed.",
				expiredNote:
					"The license server has not confirmed the license for 30 days. Check now, or enter another key.",
				overdue:
					"The license server has not confirmed the license lately. The Designer and the Operator close in {n} day unless a check succeeds. | The license server has not confirmed the license lately. The Designer and the Operator close in {n} days unless a check succeeds.",
				overdueNote: "They close on {date}.",
				open: "Open license",
			},
			errors: {
				license_key_unknown:
					"The license server does not know this key. Check it and try again.",
				license_disabled:
					"The license server says this license is disabled. Contact LiveReader.",
				license_server_unreachable:
					"The license server could not be reached. Try again later.",
				license_response_invalid: "The license server's answer could not be read.",
				license_key_missing: "No license key is stored. Enter one first.",
				invalid_license_key: "A license key is a single word, without spaces.",
			},
		},
		errors: {
			lastAdministrator:
				"After this nobody could manage users and system roles. Give someone else a role that does first.",
			lastMemberAdmin: "This is its last admin. Make someone else admin first.",
			selfRoleChange: "You cannot change your own system role. Ask another administrator.",
			roleLocked: "What this role grants is fixed. Duplicate it to make a role you can edit.",
			roleInUse: "Users still hold this role. Give them another one first.",
			roleExists: "A system role with this name exists already.",
			missingEntitlement: "Your system role does not allow this.",
			mailNotConfigured: "Mail is not configured: set a host and a sender address first.",
			llmNotConfigured: "The LLM gateway is not configured: set all four values first.",
			mapDisabled:
				"There is no map: set a tile server and turn the map on first, then test it.",
			locationNotConfigured: "No location service is set: set its URL first, then test it.",
			processesNotConfigured:
				"Processes get no gateway yet: set a URL and a token for them first.",
		},
	},
};
